import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import AudiotrackOutlinedIcon from '@mui/icons-material/AudiotrackOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    LinearProgress,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';

import { formatApiDateTime } from '../admin/dateTimeUtils';
import { FolderManagementPanel } from './FolderManagementPanel';
import { MachineStatusPanel } from './MachineStatusPanel';

const directPipelineBaseUrl = 'http://192.168.20.62:8080';
const proxyPipelineBaseUrl = '/video-pipeline-api';
const defaultPipelineBaseUrl = import.meta.env.DEV ? proxyPipelineBaseUrl : directPipelineBaseUrl;
const pipelineSettingsStorageKey = 'pteultra-admin:video-generate-settings';
const jobsTableRowLimit = 10;

type PipelineSettings = {
    baseUrl: string;
    take: string;
    fileRoot: string;
    filePath: string;
    jobId: string;
};

type RequestState = {
    loading: boolean;
    error: string;
};

type PipelineTake = {
    name: string;
    files?: PipelineFile[];
};

type PipelineFile = {
    name: string;
    path: string;
    size?: number;
    is_dir?: boolean;
    isDirectory?: boolean;
    modified_at?: string;
    modifiedAt?: string;
};

type PipelineJob = {
    id: number | string;
    take?: string;
    preset?: string;
    status?: string;
    stage?: string;
    progress?: number;
    created_at?: string;
    createdAt?: string;
    updated_at?: string;
    updatedAt?: string;
    outputs?: unknown;
};

const defaultSettings: PipelineSettings = {
    baseUrl: defaultPipelineBaseUrl,
    take: 'take3',
    fileRoot: 'outputs',
    filePath: '',
    jobId: '',
};

function trimTrailingSlash(value: string): string {
    return value.trim().replace(/\/+$/, '');
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown, fallback = ''): string {
    return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function formatJson(value: unknown): string {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'string') return value;
    return JSON.stringify(value, null, 2);
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    const record = asRecord(payload);
    const message = record.detail ?? record.message ?? record.error ?? record.errorMessage;
    return typeof message === 'string' && message.trim() ? message : fallback;
}

function readSettings(): PipelineSettings {
    if (typeof window === 'undefined') return defaultSettings;

    try {
        const parsed = JSON.parse(window.localStorage.getItem(pipelineSettingsStorageKey) ?? '{}') as Partial<PipelineSettings>;
        return {
            baseUrl: import.meta.env.DEV && parsed.baseUrl === directPipelineBaseUrl
                ? proxyPipelineBaseUrl
                : asString(parsed.baseUrl, defaultSettings.baseUrl),
            take: asString(parsed.take, defaultSettings.take),
            fileRoot: asString(parsed.fileRoot, defaultSettings.fileRoot),
            filePath: asString(parsed.filePath, defaultSettings.filePath),
            jobId: asString(parsed.jobId, defaultSettings.jobId),
        };
    } catch {
        return defaultSettings;
    }
}

function writeSettings(settings: PipelineSettings): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(pipelineSettingsStorageKey, JSON.stringify(settings));
}

function normalizeFiles(value: unknown): PipelineFile[] {
    const payload = Array.isArray(value) ? value : asRecord(value).files;
    if (!Array.isArray(payload)) return [];

    return payload.map((item) => {
        const record = asRecord(item);
        const name = asString(record.name || record.fileName || record.path);
        return {
            name,
            path: asString(record.path, name),
            size: asNumber(record.size ?? record.sizeBytes, 0),
            is_dir: Boolean(record.is_dir),
            isDirectory: Boolean(record.isDirectory ?? record.is_dir),
            modified_at: asString(record.modified_at),
            modifiedAt: asString(record.modifiedAt ?? record.modified_at),
        };
    });
}

function normalizeTakes(value: unknown): PipelineTake[] {
    const payload = Array.isArray(value) ? value : asRecord(value).takes;
    if (!Array.isArray(payload)) return [];

    return payload.map((item) => {
        if (typeof item === 'string') {
            return { name: item };
        }

        const record = asRecord(item);
        return {
            name: asString(record.name || record.take),
            files: normalizeFiles(record.files),
        };
    });
}

function normalizeJobs(value: unknown): PipelineJob[] {
    const payload = Array.isArray(value) ? value : asRecord(value).jobs;
    if (!Array.isArray(payload)) return [];

    return payload.map((item) => {
        const record = asRecord(item);
        return {
            id: asString(record.id, String(record.id ?? '')),
            take: asString(record.take),
            preset: asString(record.preset),
            status: asString(record.status),
            stage: asString(record.stage),
            progress: asNumber(record.progress, 0),
            created_at: asString(record.created_at),
            createdAt: asString(record.createdAt ?? record.created_at),
            updated_at: asString(record.updated_at),
            updatedAt: asString(record.updatedAt ?? record.updated_at),
            outputs: record.outputs,
        };
    });
}

async function requestPipeline<T>(baseUrl: string, path: string, init: RequestInit = {}): Promise<T> {
    const url = `${trimTrailingSlash(baseUrl)}${path}`;
    const headers = new Headers(init.headers);

    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }

    const response = await fetch(url, { ...init, headers });
    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    return payload as T;
}

function StatusChip({ label, ok }: { label: string; ok: boolean }) {
    return <Chip size="small" variant="outlined" color={ok ? 'success' : 'warning'} label={label} />;
}

export function VideoGeneratePage() {
    const notify = useNotify();
    const imageInputRef = useRef<HTMLInputElement | null>(null);
    const audioInputRef = useRef<HTMLInputElement | null>(null);
    const [settings, setSettings] = useState<PipelineSettings>(() => readSettings());
    const [requestState, setRequestState] = useState<RequestState>({ loading: false, error: '' });
    const [health, setHealth] = useState<unknown>(null);
    const [presets, setPresets] = useState<unknown>(null);
    const [takes, setTakes] = useState<PipelineTake[]>([]);
    const [takeDetail, setTakeDetail] = useState<unknown>(null);
    const [jobs, setJobs] = useState<PipelineJob[]>([]);
    const [jobDetail, setJobDetail] = useState<unknown>(null);
    const [jobLogs, setJobLogs] = useState('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [audioFile, setAudioFile] = useState<File | null>(null);
    const [lastResponse, setLastResponse] = useState<unknown>(null);

    useEffect(() => {
        writeSettings(settings);
    }, [settings]);

    const baseUrl = trimTrailingSlash(
        import.meta.env.DEV && settings.baseUrl === directPipelineBaseUrl
            ? proxyPipelineBaseUrl
            : settings.baseUrl || defaultPipelineBaseUrl,
    );
    const canUseJob = settings.jobId.trim().length > 0;

    const updateSettings = (patch: Partial<PipelineSettings>) => {
        setSettings((current) => ({ ...current, ...patch }));
    };

    const runRequest = async <T,>(action: () => Promise<T>, successMessage?: string): Promise<T | null> => {
        setRequestState({ loading: true, error: '' });

        try {
            const result = await action();
            setLastResponse(result);
            if (successMessage) {
                notify(successMessage, { type: 'info' });
            }
            return result;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Pipeline request failed';
            setRequestState({ loading: false, error: message });
            notify(message, { type: 'warning' });
            return null;
        } finally {
            setRequestState((current) => ({ ...current, loading: false }));
        }
    };

    const loadHealth = async () => {
        const result = await runRequest(async () => requestPipeline<unknown>(baseUrl, '/api/health'));
        setHealth(result);
    };

    const loadPresets = async () => {
        const result = await runRequest(async () => requestPipeline<unknown>(baseUrl, '/api/presets'));
        setPresets(result);
    };

    useEffect(() => {
        void loadHealth();
    }, []);

    const loadTakes = async () => {
        const result = await runRequest(async () => requestPipeline<unknown>(baseUrl, '/api/takes'));
        setTakes(normalizeTakes(result));
    };

    useEffect(() => {
        void loadTakes();
    }, []);

    const getTakeNumber = (take: string): number => {
        const match = /^take(\d+)$/i.exec(take.trim());
        return match ? Number(match[1]) : 0;
    };

    const nextTakeName = useMemo(() => {
        const maxTakeNumber = takes.reduce((max, item) => Math.max(max, getTakeNumber(item.name)), 0);
        return `take${maxTakeNumber + 1}`;
    }, [takes]);

    const selectedTake = useMemo(
        () => takes.find((take) => take.name === settings.take) ?? null,
        [settings.take, takes],
    );

    const selectedTakeFiles = selectedTake?.files ?? [];
    const selectedTakeImage = selectedTakeFiles.find((file) => /\.(png|jpe?g|webp)$/i.test(file.name));
    const selectedTakeAudio = selectedTakeFiles.find((file) => /\.(mp3|mpeg|wav|m4a|aac|ogg)$/i.test(file.name));

    const createTake = async () => {
        const take = nextTakeName;
        if (!take) {
            notify('Take name is required', { type: 'warning' });
            return;
        }

        await runRequest(
            async () =>
                requestPipeline<unknown>(baseUrl, '/api/takes', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ take }),
                }),
            `Take ${take} created`,
        );
        updateSettings({ take });
        await loadTakes();
    };

    const loadTakeDetail = async () => {
        const take = encodeURIComponent(settings.take.trim());
        if (!take) {
            notify('Take name is required', { type: 'warning' });
            return;
        }

        const result = await runRequest(async () => requestPipeline<unknown>(baseUrl, `/api/takes/${take}`));
        setTakeDetail(result);
    };

    const getUploadFileName = (kind: 'image' | 'audio', file: File): string => {
        const extension = file.name.split('.').pop()?.trim().toLowerCase();
        const fallback = kind === 'image' ? 'png' : 'mpeg';
        return `${kind}.${extension || fallback}`;
    };

    const uploadTakeAsset = async (kind: 'image' | 'audio') => {
        const take = encodeURIComponent(settings.take.trim());
        const file = kind === 'image' ? imageFile : audioFile;
        if (!take || !file) {
            notify(`Choose a take and ${kind} file before uploading`, { type: 'warning' });
            return;
        }

        const formData = new FormData();
        formData.set('file', file);
        formData.set('filename', getUploadFileName(kind, file));

        await runRequest(
            async () =>
                requestPipeline<unknown>(baseUrl, `/api/takes/${take}/files`, {
                    method: 'POST',
                    body: formData,
                }),
            `${kind === 'image' ? 'Image' : 'Audio'} uploaded`,
        );

        if (kind === 'image') {
            setImageFile(null);
            if (imageInputRef.current) {
                imageInputRef.current.value = '';
            }
        } else {
            setAudioFile(null);
            if (audioInputRef.current) {
                audioInputRef.current.value = '';
            }
        }

        await loadTakes();
        await loadTakeDetail();
    };

    const startJob = async (preset: 'production' | 'custom' | 'dry-run') => {
        const take = settings.take.trim();
        if (!take) {
            notify('Take name is required', { type: 'warning' });
            return;
        }

        const body =
            preset === 'dry-run'
                ? {
                      take,
                      preset: 'custom',
                      duration_limit: 3,
                      upscale: false,
                      interpolate_60fps: false,
                      dry_run: true,
                  }
                : {
                      take,
                      preset,
                      orientation: 'vertical',
                      native_size: '480',
                      sample_steps: 12,
                      audio_guide_scale: 4.0,
                      ...(preset === 'custom' ? { duration_limit: 3 } : {}),
                      upscale: true,
                      interpolate_60fps: true,
                      final_width: 1080,
                      final_height: 1920,
                  };

        const result = await runRequest(
            async () =>
                requestPipeline<unknown>(baseUrl, '/api/jobs', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
            'Video job started',
        );
        const nextId = asString(asRecord(result).id, String(asRecord(result).id ?? ''));
        if (nextId) {
            updateSettings({ jobId: nextId });
        }
        await loadJobs();
    };

    const loadJobs = async () => {
        const result = await runRequest(async () => requestPipeline<unknown>(baseUrl, `/api/jobs?limit=${jobsTableRowLimit}`));
        setJobs(normalizeJobs(result).slice(0, jobsTableRowLimit));
    };

    const loadJobDetail = async (jobIdInput = settings.jobId) => {
        const jobId = encodeURIComponent(jobIdInput.trim());
        if (!jobId) {
            notify('Job ID is required', { type: 'warning' });
            return;
        }

        updateSettings({ jobId: jobIdInput.trim() });
        const result = await runRequest(async () => requestPipeline<unknown>(baseUrl, `/api/jobs/${jobId}`));
        setJobDetail(result);
    };

    const loadJobLogs = async (jobIdInput = settings.jobId) => {
        const jobId = encodeURIComponent(jobIdInput.trim());
        if (!jobId) {
            notify('Job ID is required', { type: 'warning' });
            return;
        }

        updateSettings({ jobId: jobIdInput.trim() });
        const result = await runRequest(async () => requestPipeline<string>(baseUrl, `/api/jobs/${jobId}/logs?max_chars=20000`));
        setJobLogs(formatJson(result));
    };

    const cancelJob = async (jobIdInput = settings.jobId) => {
        const jobId = encodeURIComponent(jobIdInput.trim());
        if (!jobId) {
            notify('Job ID is required', { type: 'warning' });
            return;
        }

        updateSettings({ jobId: jobIdInput.trim() });
        await runRequest(
            async () =>
                requestPipeline<unknown>(baseUrl, `/api/jobs/${jobId}/cancel`, {
                    method: 'POST',
                }),
            'Cancel requested',
        );
        await loadJobDetail(jobIdInput);
    };

    const retryJob = async (jobIdInput = settings.jobId) => {
        const jobId = encodeURIComponent(jobIdInput.trim());
        if (!jobId) {
            notify('Job ID is required', { type: 'warning' });
            return;
        }

        updateSettings({ jobId: jobIdInput.trim() });
        const result = await runRequest(
            async () =>
                requestPipeline<unknown>(baseUrl, `/api/jobs/${jobId}/retry`, {
                    method: 'POST',
                }),
            'Retry job queued',
        );
        const nextId = asString(asRecord(result).id, String(asRecord(result).id ?? ''));
        if (nextId) {
            updateSettings({ jobId: nextId });
        }
        await loadJobs();
        if (nextId) {
            const detail = await runRequest(async () => requestPipeline<unknown>(baseUrl, `/api/jobs/${encodeURIComponent(nextId)}`));
            setJobDetail(detail);
        }
    };

    const canCancelJob = (job: PipelineJob): boolean => {
        const status = job.status?.trim().toLowerCase() ?? '';
        return status === 'queued' || status === 'running';
    };

    const healthOnline = useMemo(() => health !== null && !requestState.error, [health, requestState.error]);

    return (
        <Box>
            <Title title="Video Generate" />
            {requestState.loading ? <LinearProgress /> : null}
            <Stack spacing={3} sx={{ p: { xs: 2, md: 3 } }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h4">Video Generate</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Pipeline API controls for takes, uploads, video jobs, logs, and generated files.
                        </Typography>
                    </Box>
                    <StatusChip label={healthOnline ? 'Health online' : 'Health not confirmed'} ok={healthOnline} />
                </Stack>

                {requestState.error ? <Alert severity="warning">{requestState.error}</Alert> : null}

                <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }}>
                            <TextField
                                label="Pipeline Base URL"
                                value={settings.baseUrl}
                                onChange={(event) => updateSettings({ baseUrl: event.target.value })}
                                helperText={
                                    import.meta.env.DEV
                                        ? 'Local admin uses this proxy to reach http://192.168.20.62:8080 without browser CORS blocking.'
                                        : 'Use the direct pipeline API URL when the API allows browser CORS.'
                                }
                                sx={{ flex: 1, minWidth: { lg: 360 } }}
                            />
                            <Button variant="outlined" onClick={() => updateSettings({ baseUrl: proxyPipelineBaseUrl })}>
                                Use Proxy
                            </Button>
                            <Button variant="outlined" onClick={() => updateSettings({ baseUrl: directPipelineBaseUrl })}>
                                Use Direct
                            </Button>
                            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={() => void loadHealth()}>
                                Check Health
                            </Button>
                            <Button variant="outlined" onClick={() => void loadPresets()}>
                                Load Presets
                            </Button>
                        </Stack>
                        <Alert severity={healthOnline ? 'success' : 'info'}>
                            {healthOnline
                                ? `Health OK: ${formatJson(health)}`
                                : 'Health will check automatically. Use the proxy option locally if direct browser requests are blocked by CORS.'}
                        </Alert>
                    </Stack>
                </Paper>

                <MachineStatusPanel baseUrl={baseUrl} onResponse={setLastResponse} />

                <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="stretch">
                    <Paper variant="outlined" sx={{ p: 2.5, flex: 1, minWidth: 0 }}>
                        <Stack spacing={2}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="h6">Takes</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Take folders are generated incrementally. Each take expects one image and one audio file.
                                    </Typography>
                                </Box>
                                <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={() => void loadTakes()}>
                                    Refresh
                                </Button>
                            </Stack>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <TextField
                                    label="Selected Take"
                                    select
                                    value={settings.take}
                                    onChange={(event) => updateSettings({ take: event.target.value })}
                                    sx={{ flex: 1 }}
                                >
                                    {takes.map((take) => (
                                        <MenuItem key={take.name} value={take.name}>
                                            {take.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    label="Next Take"
                                    value={nextTakeName}
                                    slotProps={{ input: { readOnly: true } }}
                                    sx={{ width: { md: 180 } }}
                                />
                                <Button variant="outlined" onClick={() => void createTake()}>
                                    Create Next
                                </Button>
                                <Button variant="outlined" onClick={() => void loadTakeDetail()}>
                                    Detail
                                </Button>
                            </Stack>

                            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
                                <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                                    <Stack spacing={1.5}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <ImageOutlinedIcon color={selectedTakeImage ? 'success' : 'action'} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 800 }}>Image</Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                                                    {selectedTakeImage ? selectedTakeImage.name : 'No image uploaded'}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                            <Button variant="outlined" component="label" startIcon={<UploadFileRoundedIcon />}>
                                                {imageFile ? imageFile.name : 'Choose Image'}
                                                <input
                                                    ref={imageInputRef}
                                                    hidden
                                                    type="file"
                                                    accept="image/png,image/jpeg,image/webp"
                                                    onChange={(event: ChangeEvent<HTMLInputElement>) => setImageFile(event.target.files?.[0] ?? null)}
                                                />
                                            </Button>
                                            <Button variant="contained" onClick={() => void uploadTakeAsset('image')} disabled={!imageFile || !settings.take}>
                                                Upload Image
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Paper>

                                <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                                    <Stack spacing={1.5}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <AudiotrackOutlinedIcon color={selectedTakeAudio ? 'success' : 'action'} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 800 }}>Audio</Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                                                    {selectedTakeAudio ? selectedTakeAudio.name : 'No audio uploaded'}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                            <Button variant="outlined" component="label" startIcon={<UploadFileRoundedIcon />}>
                                                {audioFile ? audioFile.name : 'Choose Audio'}
                                                <input
                                                    ref={audioInputRef}
                                                    hidden
                                                    type="file"
                                                    accept="audio/mpeg,audio/wav,audio/x-wav,audio/mp4,audio/aac,audio/ogg"
                                                    onChange={(event: ChangeEvent<HTMLInputElement>) => setAudioFile(event.target.files?.[0] ?? null)}
                                                />
                                            </Button>
                                            <Button variant="contained" onClick={() => void uploadTakeAsset('audio')} disabled={!audioFile || !settings.take}>
                                                Upload Audio
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Paper>
                            </Stack>

                            {takes.length > 0 ? (
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {takes.map((take) => (
                                        <Chip
                                            key={take.name}
                                            label={take.name}
                                            variant={settings.take === take.name ? 'filled' : 'outlined'}
                                            onClick={() => updateSettings({ take: take.name })}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                <Alert severity="info">No takes loaded.</Alert>
                            )}
                            {selectedTakeFiles.length > 0 ? (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Current Take Files</TableCell>
                                                <TableCell>Size</TableCell>
                                                <TableCell>Modified</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedTakeFiles.map((file) => (
                                                <TableRow key={file.path}>
                                                    <TableCell>{file.name}</TableCell>
                                                    <TableCell>{file.size ? `${Math.round(file.size / 1024)} KB` : 'N/A'}</TableCell>
                                                    <TableCell>{formatApiDateTime(file.modifiedAt || file.modified_at)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : null}
                        </Stack>
                    </Paper>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <FolderManagementPanel baseUrl={baseUrl} onResponse={setLastResponse} />
                    </Box>
                </Stack>

                <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="h6">Jobs</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Production runs full audio. Custom and dry run are limited to the first 3 seconds.
                                </Typography>
                            </Box>
                            <Button variant="contained" startIcon={<PlayArrowRoundedIcon />} onClick={() => void startJob('production')}>
                                Production
                            </Button>
                            <Button variant="outlined" startIcon={<PlayArrowRoundedIcon />} onClick={() => void startJob('custom')}>
                                Custom 3s
                            </Button>
                            <Button variant="outlined" onClick={() => void startJob('dry-run')}>
                                Dry Run
                            </Button>
                        </Stack>
                        <Divider />
                        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
                            <TextField
                                label="Job ID"
                                value={settings.jobId}
                                onChange={(event) => updateSettings({ jobId: event.target.value })}
                                sx={{ minWidth: { lg: 220 } }}
                            />
                            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={() => void loadJobs()}>
                                List Jobs
                            </Button>
                            <Button variant="outlined" onClick={() => void loadJobDetail()} disabled={!canUseJob}>
                                Load Selected
                            </Button>
                        </Stack>
                        {jobs.length > 0 ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Take</TableCell>
                                            <TableCell>Preset</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Stage</TableCell>
                                            <TableCell>Progress</TableCell>
                                            <TableCell>Updated</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {jobs.map((job) => (
                                            <TableRow
                                                key={String(job.id)}
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => updateSettings({ jobId: String(job.id) })}
                                            >
                                                <TableCell>{job.id}</TableCell>
                                                <TableCell>{job.take || 'N/A'}</TableCell>
                                                <TableCell>{job.preset || 'N/A'}</TableCell>
                                                <TableCell>{job.status || 'N/A'}</TableCell>
                                                <TableCell>{job.stage || 'N/A'}</TableCell>
                                                <TableCell>{job.progress ? `${Math.round(job.progress)}%` : 'N/A'}</TableCell>
                                                <TableCell>{formatApiDateTime(job.updatedAt || job.createdAt)}</TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end" useFlexGap flexWrap="wrap">
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                void loadJobDetail(String(job.id));
                                                            }}
                                                        >
                                                            Detail
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                void loadJobLogs(String(job.id));
                                                            }}
                                                        >
                                                            Logs
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<ReplayOutlinedIcon />}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                void retryJob(String(job.id));
                                                            }}
                                                        >
                                                            Retry
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="warning"
                                                            startIcon={<CancelOutlinedIcon />}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                void cancelJob(String(job.id));
                                                            }}
                                                            disabled={!canCancelJob(job)}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Alert severity="info">No jobs loaded.</Alert>
                        )}
                    </Stack>
                </Paper>

                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
                    <Paper variant="outlined" sx={{ p: 2.5, flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ mb: 1.5 }}>
                            Latest Response
                        </Typography>
                        <Box component="pre" sx={{ m: 0, overflow: 'auto', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                            {formatJson(lastResponse || health || presets || takeDetail || jobDetail) || 'No response yet.'}
                        </Box>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 2.5, flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ mb: 1.5 }}>
                            Job Logs
                        </Typography>
                        <Box component="pre" sx={{ m: 0, maxHeight: 420, overflow: 'auto', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                            {jobLogs || 'No logs loaded.'}
                        </Box>
                    </Paper>
                </Stack>
            </Stack>
        </Box>
    );
}
